import { useEffect, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { contactApi } from '@/services/api'
import type { Contact } from '@/types'
import { Plus, Archive, RotateCcw, Search } from 'lucide-react'
import toast from 'react-hot-toast'
import { formatCurrency } from '@/utils/currency'
import { cn } from '@/lib/utils'
import { Card, CardContent } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table'

const TYPE_TABS = ['all', 'customers', 'suppliers', 'archived'] as const
type TypeTab = typeof TYPE_TABS[number]

export default function ContactList() {
  const { clientId } = useParams<{ clientId: string }>()
  const navigate = useNavigate()
  const [contacts, setContacts] = useState<Contact[]>([])
  const [activeTab, setActiveTab] = useState<TypeTab>('all')
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState('')

  useEffect(() => {
    if (!clientId) return
    setLoading(true)
    const params: Record<string, string | boolean> = {}
    if (activeTab === 'customers') params.type_filter = 'customer'
    else if (activeTab === 'suppliers') params.type_filter = 'supplier'
    if (activeTab === 'archived') params.include_archived = true
    contactApi.list(clientId, params)
      .then((res) => setContacts(res.data.contacts))
      .catch(() => toast.error('Failed to load contacts'))
      .finally(() => setLoading(false))
  }, [clientId, activeTab])

  const filtered = activeTab === 'archived'
    ? contacts.filter((c) => c.is_archived)
    : search
      ? contacts.filter((c) =>
          c.name.toLowerCase().includes(search.toLowerCase()) ||
          c.email.toLowerCase().includes(search.toLowerCase())
        )
      : contacts

  async function handleArchive(contact: Contact) {
    try {
      await contactApi.archive(contact.id)
      setContacts((prev) => prev.filter((c) => c.id !== contact.id))
      toast.success(`${contact.name} archived`)
    } catch {
      toast.error('Failed to archive')
    }
  }

  async function handleRestore(contact: Contact) {
    try {
      await contactApi.restore(contact.id)
      setContacts((prev) => prev.filter((c) => c.id !== contact.id))
      toast.success(`${contact.name} restored`)
    } catch {
      toast.error('Failed to restore')
    }
  }

  return (
    <div className="space-y-5">
      {/* Xero page header */}
      <div className="flex items-center justify-between">
        <h1 className="text-[17px] font-bold text-[#000A1E] leading-[24px]">Contacts</h1>
        <Button variant="create" onClick={() => navigate(`/clients/${clientId}/contacts/new`)}>
          <Plus className="w-4 h-4" /> New Contact
        </Button>
      </div>

      {/* Xero-style tabs + search */}
      <div className="flex items-center justify-between border-b-2 border-[#CCCED2]">
        <div className="flex gap-0">
          {TYPE_TABS.map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={cn(
                'px-4 py-3 text-[13px] transition-colors -mb-[2px] border-b-[3px] capitalize',
                activeTab === tab
                  ? 'text-[#000A1E] font-bold border-b-[#0078C8]'
                  : 'text-[#0078C8] font-normal border-b-transparent hover:text-[#000A1E]'
              )}
            >
              {tab}
            </button>
          ))}
        </div>
        {activeTab !== 'archived' && (
          <div className="relative pb-1">
            <Search className="absolute left-2.5 top-2 w-3.5 h-3.5 text-[rgba(0,10,30,0.75)]" />
            <Input
              placeholder="Search contacts..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="h-8 w-48 pl-8"
            />
          </div>
        )}
      </div>

      {/* Content panel */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-5 space-y-3">
              {[1, 2, 3].map((i) => <Skeleton key={i} className="h-12 w-full" />)}
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="hover:bg-transparent">
                  <TableHead>Name</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Phone</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead className="text-right">Outstanding</TableHead>
                  <TableHead className="text-center">Activity</TableHead>
                  <TableHead />
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} className="py-10 text-center text-[13px] text-[rgba(0,10,30,0.75)]">
                      {activeTab === 'archived' ? 'No archived contacts' : search ? 'No contacts match your search' : 'No contacts yet'}
                    </TableCell>
                  </TableRow>
                ) : (
                  filtered.map((c) => (
                    <TableRow key={c.id}>
                      <TableCell>
                        <button
                          onClick={() => navigate(`/clients/${clientId}/contacts/${c.id}/edit`)}
                          className="text-[13px] font-bold text-[#0078C8] hover:underline"
                        >
                          {c.name}
                        </button>
                        {(c.first_name || c.last_name) && (
                          <p className="text-[12px] text-[rgba(0,10,30,0.75)]">{c.first_name} {c.last_name}</p>
                        )}
                      </TableCell>
                      <TableCell className="text-[rgba(0,10,30,0.75)]">
                        {c.email || <span className="text-[#CCCED2]">—</span>}
                      </TableCell>
                      <TableCell className="text-[rgba(0,10,30,0.75)]">
                        {c.phone || c.mobile || <span className="text-[#CCCED2]">—</span>}
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-1">
                          {c.is_customer && (
                            <Badge variant="info" className="text-[10px]">
                              Customer
                            </Badge>
                          )}
                          {c.is_supplier && (
                            <Badge className="bg-purple-50 text-purple-700 text-[10px]">
                              Supplier
                            </Badge>
                          )}
                          {!c.is_customer && !c.is_supplier && (
                            <span className="text-[#CCCED2]">—</span>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="space-y-0.5">
                          {parseFloat(c.outstanding_receivable) > 0 && (
                            <p className="text-[12px] text-[#00823C] font-bold">
                              {formatCurrency(c.outstanding_receivable, c.default_currency || 'USD')} owed
                            </p>
                          )}
                          {parseFloat(c.outstanding_payable) > 0 && (
                            <p className="text-[12px] text-[#CC0000] font-bold">
                              {formatCurrency(c.outstanding_payable, c.default_currency || 'USD')} owing
                            </p>
                          )}
                          {parseFloat(c.outstanding_receivable) === 0 && parseFloat(c.outstanding_payable) === 0 && (
                            <span className="text-[#CCCED2]">—</span>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="text-center">
                        <div className="flex items-center justify-center gap-2 text-[12px] text-[rgba(0,10,30,0.75)]">
                          {c.total_invoices > 0 && <span>{c.total_invoices} inv</span>}
                          {c.total_bills > 0 && <span>{c.total_bills} bill</span>}
                          {c.total_purchase_orders > 0 && <span>{c.total_purchase_orders} PO</span>}
                          {c.total_invoices === 0 && c.total_bills === 0 && c.total_purchase_orders === 0 && (
                            <span className="text-[#CCCED2]">—</span>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1 justify-end">
                          {activeTab === 'archived' ? (
                            <Button variant="ghost" size="icon-sm" onClick={() => handleRestore(c)} title="Restore">
                              <RotateCcw className="w-3.5 h-3.5 text-[#0078C8]" />
                            </Button>
                          ) : (
                            <Button variant="ghost" size="icon-sm" onClick={() => handleArchive(c)} title="Archive"
                              className="text-[#A6A9B0] hover:text-[#856404] hover:bg-[#FFF3CD]">
                              <Archive className="w-3.5 h-3.5" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
